import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, PlusIcon, WandSparklesIcon, XIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  AUDIENCE_LABELS,
  SCOPE,
  type SkillEntry,
  type SkillScope,
  type SkillsResponse,
} from '@/pages/assistant/shared';

/**
 * The skills list — same grammar as the Events workspace. Each row links to
 * the skill's own view page (/assistant/skills/:scope/:name); editing happens
 * on a separate edit route. Permissioning is enforced by the API from the
 * verified token — this page only hides affordances the server would refuse.
 */

type ScopeFilter = 'all' | SkillScope;

/** The skill fields the search box matches. */
const SKILL_SEARCH_FIELDS: ReadonlyArray<keyof SkillEntry> = ['description', 'name'];

export function SkillsPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [deleting, setDeleting] = useState<SkillEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { search, setSearch } = useUrlSearch();

  const skills = useQuery({
    queryKey: ['assistant-skills'],
    queryFn: ({ signal }) => getJson<SkillsResponse>('/assistant/skills', undefined, signal),
  });
  const canManageGlobal = skills.data?.canManageGlobal === true;

  const remove = useMutation({
    mutationFn: (entry: SkillEntry) =>
      sendJson(
        'delete',
        `/assistant/skills/file?name=${encodeURIComponent(entry.name)}&scope=${entry.scope}`,
      ),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-skills'] });
    },
    onError: (error) => {
      setDeleting(null);
      setNotice(errorMessage(error));
    },
  });

  const scoped = (skills.data?.skills ?? []).filter(
    (skill) => scopeFilter === 'all' || skill.scope === scopeFilter,
  );
  const rows = filterRows(scoped, search, SKILL_SEARCH_FIELDS);

  return (
    <TooltipProvider delayDuration={100}>
      <section className="animate-portal-rise">
        <div className="flex items-start justify-between gap-4">
          <PageHeading
            eyebrow="AI ASSISTANT"
            title="Skills"
            subtitle="Reusable workflows the assistant follows — create them here or by describing them in chat."
          />
          <Button onClick={() => navigate('/assistant/skills/new')}>
            <PlusIcon aria-hidden className="size-4" /> New skill
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search skills"
            placeholder="Description or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
          <SegmentedControl
            label="Which skills to show"
            options={[
              { key: 'all', label: 'All' },
              { key: SCOPE.user, label: 'Personal' },
              { key: SCOPE.global, label: 'Organization' },
            ]}
            value={scopeFilter}
            onChange={(next) => setScopeFilter(next as ScopeFilter)}
          />
          {skills.data === undefined ? null : (
            <span className="text-portal-micro text-portal-muted">
              {rows.length} {rows.length === 1 ? 'skill' : 'skills'}
            </span>
          )}
        </div>

        {notice === null ? null : (
          <div className="mb-3">
            <ErrorNotice inline title="Something went wrong" message={notice} />
          </div>
        )}

        {skills.isError ? (
          <ErrorNotice title="Skills unavailable" message={errorMessage(skills.error)} />
        ) : skills.isPending ? (
          <SkeletonTable label="Loading skills…" />
        ) : rows.length === 0 && scoped.length > 0 ? (
          <Panel>
            <EmptyState title="No skills match" message="Try a different search." />
          </Panel>
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<WandSparklesIcon aria-hidden className="size-6" />}
              title={scopeFilter === SCOPE.global ? 'No organization skills yet' : 'No skills yet'}
              message={
                scopeFilter === SCOPE.global
                  ? canManageGlobal
                    ? 'Create one with scope "Organization", or promote one of your personal skills from its page.'
                    : 'Admins can create org-wide skills or promote personal ones.'
                  : 'Describe a repeatable workflow to the assistant — "save this as a skill" — or create one here.'
              }
            />
          </Panel>
        ) : (
          <DataTable headers={['Skill', 'Scope', 'Audience', 'Version', '']} minWidth={760}>
            {rows.map((skill) => {
              const canEdit = skill.scope === SCOPE.user || canManageGlobal;
              return (
                <tr
                  key={`${skill.scope}-${skill.name}`}
                  className="border-t border-portal-line align-top"
                >
                  <td className="p-3">
                    <Link
                      to={`/assistant/skills/${skill.scope}/${skill.name}`}
                      className="text-portal-small font-bold text-brand hover:underline"
                    >
                      {skill.description}
                    </Link>
                    <span className="block font-mono text-portal-micro text-portal-muted">
                      {skill.name}
                    </span>
                  </td>
                  <td className="p-3">
                    <Pill tone={skill.scope === SCOPE.global ? 'info' : 'neutral'}>
                      {skill.scope === SCOPE.global ? 'Organization' : 'Personal'}
                    </Pill>
                  </td>
                  <td className="p-3 text-portal-small">
                    {skill.scope === SCOPE.global ? AUDIENCE_LABELS[skill.audience] : '—'}
                  </td>
                  <td className="p-3 text-portal-small">v{skill.version}</td>
                  <td className="p-3">
                    {canEdit ? (
                      <span className="flex items-center justify-end gap-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/assistant/skills/${skill.scope}/${skill.name}/edit`}
                              aria-label="Edit skill"
                              className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                            >
                              <PencilIcon aria-hidden className="size-3.5" />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">Edit</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="Delete skill"
                              onClick={() => setDeleting(skill)}
                              className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                            >
                              <XIcon aria-hidden className="size-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">Delete</TooltipContent>
                        </Tooltip>
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        )}

        <ConfirmDialog
          open={deleting !== null}
          tone="danger"
          title="Delete this skill?"
          message={deleting ? `"${deleting.name}" will be removed for good.` : ''}
          confirmLabel="Delete skill"
          onConfirm={() => {
            if (deleting) remove.mutate(deleting);
          }}
          onCancel={() => {
            if (!remove.isPending) setDeleting(null);
          }}
          pending={remove.isPending}
        />
      </section>
    </TooltipProvider>
  );
}
